import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { pipe } from "ramda";
import {
  ACKNOWLEDGE_SUBMISSION_COMPLETE,
  CANCEL_UPLOAD,
  addAnotherWork,
} from "submission/submissionActions";
import {
  getUploadDialogVisible,
  getUploadComplete,
  getUploadProgress,
  getUploadStep,
} from "common/selectors";

import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import CheckIcon from "@material-ui/icons/Check";

import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { getFieldOptions } from "../../common/selectors";

const useStyles = makeStyles(theme => ({
  dialog: {
    margin: 0,

    [theme.breakpoints.up("xs")]: {
      minWidth: 300,
      minHeight: 350,
    },

    [theme.breakpoints.up("sm")]: {
      minWidth: 400,
      minHeight: 450,
    },
  },

  body: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  circle: {
    display: "flex",
    flexDirection: "column",
    width: 200,
    height: 200,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.green.light,
    justifyContent: "center",
    alignItems: "center",
  },

  doneIcon: {
    fontSize: 80,
    fontWeight: "bold",
    color: theme.palette.primary.green.dark,
  },

  percentText: {
    fontSize: 50,
    fontWeight: "bold",
    color: theme.palette.secondary.main,
  },

  completeLabel: {
    fontSize: "1.13rem",
    color: theme.palette.primary.green.dark,
    fontWeight: "bold",
    textTransform: "uppercase",
    marginTop: theme.spacing(2),
  },

  uploadingLabel: {
    fontSize: "1.13rem",
    color: theme.palette.primary.green.dark,
    fontWeight: "bold",
    textTransform: "uppercase",
    textAlign: "center",
    marginTop: theme.spacing(2),
  },

  actionArea: {
    display: "flex",
    justifyContent: "center",
  },

  actions: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
  },

  actionButton: {
    margin: theme.spacing(2),
  },

  submitAnotherButton: {
    margin: theme.spacing(2),
    fontSize: "1rem",
  },
}));

const UploadDialog = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const visible = useSelector(getUploadDialogVisible);
  const complete = useSelector(getUploadComplete);
  const progress = useSelector(getUploadProgress);
  const uploadStep = useSelector(getUploadStep);
  const fieldOptions = useSelector(getFieldOptions);

  const hideSubmitAnotherWork =
    fieldOptions?.submit_another_work_button &&
    fieldOptions?.submit_another_work_button?.hidden;

  const dispatch = useDispatch();
  const onAddAnother = pipe(addAnotherWork, dispatch);
  const onCancel = () => dispatch({ type: CANCEL_UPLOAD });
  const onOk = () => dispatch({ type: ACKNOWLEDGE_SUBMISSION_COMPLETE });

  return (
    <Dialog
      open={visible}
      onClose={onCancel}
      aria-labelledby="submission-dialog-title"
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle id="submission-dialog-title">
        {complete
          ? "Your submission was successfully uploaded!"
          : "Uploading..."}
      </DialogTitle>
      <DialogContent classes={{ root: classes.body }}>
        {complete ? (
          <div className={classes.circle}>
            <CheckIcon className={classes.doneIcon} value="check" />
            <span className={classes.completeLabel}>
              {t("submission_upload_submitted")}
            </span>
          </div>
        ) : progress < 100 ? (
          <div className={classes.circle}>
            <span className={classes.percentText}>{progress}%</span>
            <div className={classes.uploadingLabel}>
              <div>{t("submission_upload_uploading")}</div>
              <div>{uploadStep}</div>
            </div>
          </div>
        ) : (
          <div className={classes.circle}>
            <CircularProgress size={60} color="secondary" />
            <span className={classes.completeLabel}>
              {t("submission_upload_processing")}
            </span>
          </div>
        )}
      </DialogContent>
      <DialogActions
        classes={{
          root: classes.actionArea,
        }}
      >
        {complete ? (
          <div className={classes.actions}>
            <Button
              classes={{ root: classes.actionButton }}
              variant="contained"
              color="secondary"
              onClick={onOk}
            >
              {t("submission_upload_done")}
            </Button>

            {!hideSubmitAnotherWork && (
              <Button
                classes={{ root: classes.submitAnotherButton }}
                variant="text"
                color="primary"
                onClick={onAddAnother}
              >
                {t("submission_upload_submit_another")}
              </Button>
            )}
          </div>
        ) : (
          <div className={classes.actions}>
            <Button
              classes={{ root: classes.actionButton }}
              variant="contained"
              color="primary"
              onClick={onCancel}
            >
              {t("submission_upload_cancel")}
            </Button>
          </div>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadDialog;
